import { useState } from 'react'

import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

interface TimezoneSelectProps {
  value: string | undefined
  onChange: (value: string) => void
  disabled?: boolean
  id?: string
  className?: string
  label?: string
  onResolveFromLocation?: () => Promise<string | null>
  resolveDisabledReason?: string
}

const ZONES: { value: string; label: string }[] = [
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Phoenix', label: 'Mountain — Arizona (no DST)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Anchorage', label: 'Alaska' },
  { value: 'Pacific/Honolulu', label: 'Hawaii' },
]

/**
 * Human label for an IANA zone. Listed zones use their picker label; any other
 * zone (Google resolves addresses to zones like `America/Detroit` or
 * `America/Indiana/Indianapolis`) gets its generic name plus city, e.g.
 * "Eastern Time — Detroit", so a resolved zone never renders as a blank select.
 */
export function timezoneLabel(timeZone: string): string {
  const listed = ZONES.find((z) => z.value === timeZone)
  if (listed) return listed.label
  const city = timeZone.split('/').pop()?.replace(/_/g, ' ') ?? timeZone
  try {
    const name = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longGeneric' })
      .formatToParts(new Date())
      .find((part) => part.type === 'timeZoneName')?.value
    return name ? `${name} — ${city}` : timeZone
  } catch {
    return timeZone
  }
}

export function TimezoneSelect({
  value,
  onChange,
  disabled,
  id,
  className,
  label,
  onResolveFromLocation,
  resolveDisabledReason,
}: TimezoneSelectProps) {
  const [resolveState, setResolveState] = useState<'idle' | 'loading' | 'no-result'>('idle')

  const handleResolve = async () => {
    if (!onResolveFromLocation || resolveDisabledReason) return
    setResolveState('loading')
    try {
      const tz = await onResolveFromLocation()
      if (tz) {
        onChange(tz)
        setResolveState('idle')
      } else {
        setResolveState('no-result')
      }
    } catch {
      setResolveState('no-result')
    }
  }

  const linkDisabled = !!resolveDisabledReason || resolveState === 'loading'

  const select = (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger id={id} className={cn('w-full', className)} data-slot="timezone-select">
        <SelectValue placeholder="Select timezone" />
      </SelectTrigger>
      <SelectContent>
        {value && !ZONES.some((z) => z.value === value) && (
          <SelectItem value={value}>{timezoneLabel(value)}</SelectItem>
        )}
        {ZONES.map((z) => (
          <SelectItem key={z.value} value={z.value}>
            {z.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )

  if (!label) return select

  return (
    <div className="grid gap-3">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={id}>{label}</Label>
        {onResolveFromLocation && (
          <button
            type="button"
            onClick={handleResolve}
            disabled={linkDisabled}
            title={resolveDisabledReason ?? undefined}
            className={cn(
              'text-primary text-xs hover:underline disabled:cursor-not-allowed disabled:no-underline disabled:opacity-50',
            )}
          >
            {resolveState === 'loading'
              ? 'Resolving…'
              : resolveState === 'no-result'
                ? 'Not found — try again'
                : 'Use location timezone'}
          </button>
        )}
      </div>
      {select}
    </div>
  )
}
